import { NgModule } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { MenubarModule } from 'primeng/menubar';
import { ToolbarModule } from 'primeng/toolbar';
import { ClickOutsideModule } from 'ng-click-outside';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';

import { AttributeListComponent } from './components/AttributeList/attribute-list.component';
import { LocalizationModule } from '../../Core/Localization/localization.module';
import { ContentSharingComponent } from './components/ContentSharing/content-sharing.component';
import { ImageListComponent } from './components/ImageList/image-list.component';
import { PaginatorComponent } from './components/Paginator/paginator.component';
import { MinimalPaginatorComponent } from './components/MinimalPaginator/minimal-paginator.component';
import { LoadingIndicatorComponent } from './components/loadingIndicator/loading-indicator.component';
import { LoadingDirective } from './directives/loading.directive';
import { PopoverComponent } from './components/popover/popover.component';
import { PopoverDirective } from './directives/popover.directive';
import { PopoversService } from './services/popovers.service';
import { BypassURLSecurityPipe } from './pipes/BypassURLSecurity/bypass-url-security.pipe';
import { ChangeTimeZonePipe } from './pipes/ChangeTimeZone/change-time-zone.pipe';
import { UsersModule } from '../../Core/Users/users.module';
import { HighlightPipe } from './pipes/Highlight/highlight.pipe';
import { IframeResizerDirective } from './directives/iframe-resizer.directive';
import { SignificantDigitsPipe } from './pipes/SignificantDigits/significant-digits.pipe';
import { MessageComponent } from './components/Message/message.component';
import { HighlightKeywordsPipe } from './pipes/Highlight/highlight-keywords.pipe';
import { IndexHighlightPipe, IndexHighlightPipeSup, IndexHighlightApiPipe } from './pipes/Highlight/indexHighlight.pipe';
import { GrowlerComponent } from './components/Growler/growler.component';
import { GrowlComponent } from './components/Growl/growl.component';
import { ClickUnlessDirective } from './directives/click-unless.directive';
import { CommonBasicModule } from '../Basic/basic.module';
import { SortOrderComponent } from './components/SortOrder/sort-order.component';
import { FilterSummaryComponent } from './components/FilterSummary/filter-summary.component';
import { TwitterAvatarSizePipe } from './pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { IncludesPipe } from './pipes/Includes/includes.pipe';
import { IsNaNPipe } from './pipes/IsNaN/isNaN.pipe';
import { CommonContactOutletCardComponent } from './components/CommonContactOutletCard/common-contact-outlet-card.component';
import { BypassSecurityTrustHtmlPipe } from './pipes/BypassSecurityTrustHtml/bypass-security-trust-html.pipe';
import { JoinList } from './pipes/JoinList/join-list.pipe';
import { RelativeTimePipe } from './pipes/RelativeTime/relative-time.pipe';
import { ShadowDomContainerComponent } from './components/ShadowDomContainer/shadow-dom-container.component';
import { DefaultImageDirective } from './directives/default-image.directive';
import { ProminenceLockerService } from './services/prominence-locker.service';
import { CreateNewActivateGuard } from './services/create-new-activate.guard';
import { ChipTooltipPipe } from './pipes/TooltipPipes/chip-tooltip.pipe';
import { ListTooltipPipe } from './pipes/TooltipPipes/list-tooltip.pipe';
import { AbsPipe } from './pipes/AbsolutePipe/absolute.pipe';
import { CurrencyConversionPipe } from './pipes/CurrencyConversion/currency-conversion.pipe';
import { AppendCurrencyCodePipe } from './pipes/CurrencyConversion/currency-code.pipe';
import { MediaMarketsDisplayPipe } from './pipes/MediaMarketsDisplay/media-markets-display.pipe';
import { TextToLinkPipe } from './pipes/Linkify/text-to-link.pipe';
import { ListKeyboardNavigationDirective } from './directives/list-keyboard-navigation.directive';
import { AccessibleCodeMirrorComponent } from './components/AccessibleCodeMirror/accessible-code-mirror.component';
import { SetUlStylePipe } from './pipes/setUlStyle/set-ul-style.pipe';
import { HubspotFormService } from './services/hubspot-form.service';
import { PendoService } from './services/pendo.service';
import { MentionLinksPipe } from './pipes/MentionLinksPipe/mention-links.pipe';
import { GroupByPipe } from './pipes/GroupBy/group-by.pipe';
import { TitleDisplayComponent } from './components/TitleDisplay/title-display.component';
import { CommonComponentReaderComponent } from './components/CommonComponentReader/common-component-reader.component';
import { TextDisplayComponent } from './components/TextDisplay/text-display.component';
import { ImageDisplayComponent } from './components/ImageDisplay/image-display.component';
import { TableOfContentsDisplayComponent } from './components/TableOfContentsDisplay/table-of-contents-display.component';
import { DateDisplayComponent } from './components/DateDisplay/date-display.component';
import { DateStringPipe } from './pipes/DateString/date-string.pipe';
import { MentionGridDisplayComponent } from '../../Common/PageBuilder/components/MentionGridDisplay/mention-grid-display.component';
import { DividerDisplayComponent } from './components/DividerDisplay/divider-display.component';
import { SocialLinksDisplayComponent } from './components/SocialLinksDisplay/socialLinks-display.component';
import { OriginalTweetComponent } from './components/OriginalTweetText/original-tweet-text.component';
import { IsNumberPipe } from './pipes/IsNumberPipe/is-number.pipe';

export const miscImports = [
    CommonModule,
    FormsModule,
    LocalizationModule,
    UsersModule,
    TooltipModule,
    MenubarModule,
    PopoverModule,
    AutoCompleteModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToolbarModule,
    ClickOutsideModule,
    CommonBasicModule,
    CodemirrorModule
];

export const miscDeclarations = [
    AttributeListComponent,
    ContentSharingComponent,
    ImageListComponent,
    PaginatorComponent,
    MinimalPaginatorComponent,
    LoadingDirective,
    DefaultImageDirective,
    LoadingIndicatorComponent,
    PopoverComponent,
    PopoverDirective,
    ListKeyboardNavigationDirective,
    BypassURLSecurityPipe,
    BypassSecurityTrustHtmlPipe,
    ChangeTimeZonePipe,
    HighlightPipe,
    HighlightKeywordsPipe,
    IndexHighlightPipe,
    IndexHighlightPipeSup,
    IndexHighlightApiPipe,
    IframeResizerDirective,
    SignificantDigitsPipe,
    MessageComponent,
    GrowlerComponent,
    GrowlComponent,
    ClickUnlessDirective,
    SortOrderComponent,
    FilterSummaryComponent,
    TwitterAvatarSizePipe,
    IncludesPipe,
    IsNaNPipe,
    CommonContactOutletCardComponent,
    JoinList,
    RelativeTimePipe,
    DateStringPipe,
    ShadowDomContainerComponent,
    ChipTooltipPipe,
    ListTooltipPipe,
    CurrencyConversionPipe,
    AppendCurrencyCodePipe,
    MediaMarketsDisplayPipe,
    AccessibleCodeMirrorComponent,
    TextToLinkPipe,
    SetUlStylePipe,
    MentionLinksPipe,
    GroupByPipe,
    CommonComponentReaderComponent,
    TitleDisplayComponent,
    TextDisplayComponent,
    ImageDisplayComponent,
    TableOfContentsDisplayComponent,
    DateDisplayComponent,
    MentionGridDisplayComponent,
    DividerDisplayComponent,
    SocialLinksDisplayComponent,
    OriginalTweetComponent,
    IsNumberPipe,
    AbsPipe
];

@NgModule({
    imports: miscImports,
    declarations: miscDeclarations,
    exports: [
        AttributeListComponent,
        ContentSharingComponent,
        ImageListComponent,
        PaginatorComponent,
        MinimalPaginatorComponent,
        LoadingDirective,
        DefaultImageDirective,
        ListKeyboardNavigationDirective,
        LoadingIndicatorComponent,
        PopoverComponent,
        PopoverDirective,
        BypassURLSecurityPipe,
        BypassSecurityTrustHtmlPipe,
        ChangeTimeZonePipe,
        HighlightPipe,
        HighlightKeywordsPipe,
        IndexHighlightPipe,
        IndexHighlightPipeSup,
        IndexHighlightApiPipe,
        IframeResizerDirective,
        SignificantDigitsPipe,
        MessageComponent,
        GrowlerComponent,
        GrowlComponent,
        ClickUnlessDirective,
        SortOrderComponent,
        FilterSummaryComponent,
        TwitterAvatarSizePipe,
        IncludesPipe,
        IsNaNPipe,
        CommonContactOutletCardComponent,
        JoinList,
        RelativeTimePipe,
        DateStringPipe,
        ShadowDomContainerComponent,
        ChipTooltipPipe,
        ListTooltipPipe,
        CurrencyConversionPipe,
        AppendCurrencyCodePipe,
        MediaMarketsDisplayPipe,
        AccessibleCodeMirrorComponent,
        SetUlStylePipe,
        TextToLinkPipe,
        MentionLinksPipe,
        GroupByPipe,
        CommonComponentReaderComponent,
        TitleDisplayComponent,
        TextDisplayComponent,
        ImageDisplayComponent,
        DateDisplayComponent,
        MentionGridDisplayComponent,
        DividerDisplayComponent,
        SocialLinksDisplayComponent,
        OriginalTweetComponent,
        IsNumberPipe,
        AbsPipe
    ],
    providers: [
        DecimalPipe,
        SignificantDigitsPipe,
        HighlightPipe,
        HighlightKeywordsPipe,
        IndexHighlightPipe,
        IndexHighlightPipeSup,
        IndexHighlightApiPipe,
        PopoversService,
        TwitterAvatarSizePipe,
        ProminenceLockerService,
        CreateNewActivateGuard,
        ChipTooltipPipe,
        ListTooltipPipe,
        MediaMarketsDisplayPipe,
        HubspotFormService,
        PendoService
    ]
})
export class MiscModule { }
